/*
 * Reading 2 capture (screen S6C).
 *
 * Recovers the first reading from localStorage or the resume link, waits out
 * the retest window, then records the second band and the delta between them.
 * This is the payoff of the booth mechanic, so it is written to survive a
 * closed browser, a different device and a hostile network.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'bl_event_quiz';
  var STATE_VERSION = 1;
  /* 2024-10-15 rejects the `subscriptions` object on the profile inside a
     client subscription with a 400, which silently dropped every list
     signup. 2025-01-15 is the earliest revision that accepts it. */
  var KLAVIYO_REVISION = '2025-01-15';
  var REQUEST_TIMEOUT = 8000;
  var BANDS = ['depleted', 'low', 'threshold', 'adequate', 'good', 'optimal'];

  function readStored() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function writeStored(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) {
      // The submit below does not depend on storage succeeding.
    }
  }

  function postJson(url, body) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT) : null;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body),
      keepalive: true,
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (timer) clearTimeout(timer);
      if (!response.ok) throw new Error('Klaviyo responded ' + response.status);
      return response;
    }).catch(function (error) {
      if (timer) clearTimeout(timer);
      throw error;
    });
  }

  function postWithRetry(url, body) {
    return postJson(url, body).catch(function (error) {
      return new Promise(function (resolve) { setTimeout(resolve, 800); })
        .then(function () { return postJson(url, body); })
        .catch(function () { throw error; });
    });
  }

  customElements.define('bl-event-retest', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.form = this.querySelector('[data-retest-form]');
      if (!this.form) return;

      this.ready = true;
      this.root = this.closest('[data-sl-id="sl-section-events-retest-v1"]');
      this.state = this.hydrate();

      this.onSubmit = this.handleSubmit.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.addEventListener('change', this.onChange);

      /* This element is in the DOM from first paint but only becomes visible
         a stage later, so the reading it describes usually lands long after it
         was constructed. Without these it would keep showing whatever storage
         held at page load, which is nothing at all for a first-time visitor. */
      this.onRefresh = this.refresh.bind(this);
      document.addEventListener('bl:event-quiz:submitted', this.onRefresh);
      document.addEventListener('bl:event-phase', this.onRefresh);

      this.markFirstReading();
      this.renderEyebrow();
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('bl:event-quiz:submitted', this.onRefresh);
      document.removeEventListener('bl:event-phase', this.onRefresh);
      this.ready = false;
    }

    /* Re-reads storage after the first reading is taken. The band already
       picked here lives in the checked radio, not in state, so rebuilding
       state cannot lose it. */
    refresh() {
      // Once this element has its own second reading there is nothing left to
      // pick up, and re-reading storage could only pull in another session.
      if (this.busy || this.state.reading2) return;
      this.state = this.hydrate();
      this.markFirstReading();
      this.renderEyebrow();
    }

    /* ---------- state ---------- */

    /* localStorage first, then the resume link. The link is what makes this
       work on a second device or after the cache is cleared. */
    hydrate() {
      var saved = readStored();
      var state = saved && saved.v === STATE_VERSION ? saved : { v: STATE_VERSION };
      var params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (_) {
        params = null;
      }
      if (!params) return state;

      var sid = params.get('sid');
      var band = params.get('r1');
      var at = parseInt(params.get('t'), 10);

      // A band from the URL is only ever accepted if it is one of the six.
      if (BANDS.indexOf(band) === -1) band = null;
      if (!Number.isFinite(at) || at <= 0) at = null;

      // A link for a different session wins: the person followed it on purpose.
      if (sid && state.sessionId && state.sessionId !== sid) {
        state = { v: STATE_VERSION, sessionId: sid };
      }
      if (sid && !state.sessionId) state.sessionId = sid;
      if (band && !state.reading1) state.reading1 = band;
      if (at && !state.reading1At) state.reading1At = at;

      if (state.reading1 && state.reading1Index == null) {
        state.reading1Index = BANDS.indexOf(state.reading1);
      }
      return state;
    }



    markFirstReading() {
      var fallback = this.querySelector('[data-retest-fallback]');
      // Runs again whenever the reading changes, so each branch has to undo the
      // other rather than only ever revealing things.
      var badges = this.form.querySelectorAll('[data-option-badge]');
      for (var i = 0; i < badges.length; i++) badges[i].hidden = true;

      if (!this.state.reading1) {
        // No diff is possible. Say so instead of rendering a broken before
        // and after later on.
        if (fallback) fallback.hidden = false;
        return;
      }
      if (fallback) fallback.hidden = true;

      var input = this.form.querySelector('[name="reading_2"][value="' + this.state.reading1 + '"]');
      if (!input) return;
      var badge = input.parentElement.querySelector('[data-option-badge]');
      if (badge) badge.hidden = false;
    }

    renderEyebrow() {
      var eyebrow = this.root && this.root.querySelector('[data-retest-eyebrow]');
      if (!eyebrow || !this.state.reading1At) return;
      var time = new Date(this.state.reading1At * 1000)
        .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      eyebrow.textContent = (this.root.dataset.eyebrow || 'Your reading') + ' · ' + time;
    }

    /* ---------- interaction ---------- */

    handleChange() {
      var slot = this.querySelector('[data-retest-error]');
      if (slot) slot.hidden = true;
    }

    selectedBand() {
      var picked = this.form.querySelector('[name="reading_2"]:checked');
      return picked ? picked.value : null;
    }

    showError(message) {
      var slot = this.querySelector('[data-retest-error]');
      if (!slot) return;
      slot.textContent = message;
      slot.hidden = false;
    }

    handleSubmit(event) {
      event.preventDefault();
      if (this.busy) return;

      var band = this.selectedBand();
      if (BANDS.indexOf(band) === -1) {
        this.showError('Select the band that matches your second strip.');
        return;
      }

      var now = Math.floor(Date.now() / 1000);
      this.state.reading2 = band;
      this.state.reading2Index = BANDS.indexOf(band);
      this.state.reading2At = now;
      this.state.retestSynced = false;
      writeStored(this.state);

      var button = this.querySelector('[data-retest-submit]');
      var original = button ? button.textContent : '';
      this.busy = true;
      if (button) {
        button.disabled = true;
        button.textContent = 'Sending…';
      }

      var self = this;
      this.submitReading2()
        .catch(function (error) {
          // The reading is already stored, so a failed send must never block
          // the before and after the visitor came back for.
          if (window.console) console.warn('[bl-event-retest] Klaviyo send failed', error);
        })
        .then(function () {
          self.busy = false;
          if (button) {
            button.disabled = false;
            button.textContent = original;
          }
          self.finish();
        });
    }

    /* ---------- Klaviyo ---------- */

    isDryRun() {
      return this.root && this.root.dataset.dryRun === 'true';
    }

    eventProperties() {
      var state = this.state;
      var props = {
        reading_1: state.reading1 || null,
        reading_2: state.reading2,
        reading_1_index: state.reading1Index == null ? null : state.reading1Index,
        reading_2_index: state.reading2Index,
        session_id: state.sessionId || null,
        source: this.root ? this.root.dataset.source || null : null,
        event_name: this.root ? this.root.dataset.eventName || null : null
      };

      // delta and improved only mean something with both readings in hand.
      if (state.reading1Index != null && state.reading1Index >= 0) {
        props.delta = state.reading2Index - state.reading1Index;
        props.improved = props.delta > 0;
      }
      if (state.reading1At && state.reading2At) {
        props.minutes_elapsed = Math.round((state.reading2At - state.reading1At) / 60);
      }
      return props;
    }

    submitReading2() {
      var account = this.root && this.root.dataset.klaviyoAccount;
      if (!account) return Promise.reject(new Error('Klaviyo account id missing'));

      var props = this.eventProperties();
      var self = this;

      if (this.isDryRun()) {
        this.state.dryRun = true;
        if (window.console) {
          console.warn('[bl-event-retest] Dry run. Nothing sent. Payload that would have gone out:', props);
        }
        // Held briefly so the submit animation runs the same as in production.
        return new Promise(function (resolve) { setTimeout(resolve, 600); })
          .then(function () { self.markSynced(); });
      }

      var base = 'https://a.klaviyo.com/client/';
      var query = '/?company_id=' + encodeURIComponent(account);
      var identifier = this.profileAttributes();
      if (!identifier) {
        // Without an email the event cannot attach to the profile that holds
        // reading 1, and Klaviyo drops unidentified events silently.
        return Promise.reject(new Error('No identifier for the retest profile'));
      }

      return postWithRetry(base + 'profiles' + query, {
        data: {
          type: 'profile',
          attributes: Object.assign({}, identifier, {
            properties: {
              no_reading_2: this.state.reading2,
              no_reading_2_index: this.state.reading2Index,
              no_delta: props.delta == null ? null : props.delta,
              no_improved: props.improved == null ? null : props.improved
            }
          })
        }
      }).then(function () {
        return postWithRetry(base + 'events' + query, {
          data: {
            type: 'event',
            attributes: {
              properties: props,
              unique_id: (self.state.sessionId || 'no-session') + '-reading-2',
              time: new Date(self.state.reading2At * 1000).toISOString(),
              metric: { data: { type: 'metric', attributes: { name: 'NO Reading 2 Submitted' } } },
              profile: { data: { type: 'profile', attributes: identifier } }
            }
          }
        });
      }).then(function () {
        self.markSynced();
      });
    }

    profileAttributes() {
      var state = this.state;
      if (state.email) {
        var attributes = { email: state.email };
        if (state.firstName) attributes.first_name = state.firstName;
        if (state.phone) attributes.phone_number = state.phone;
        return attributes;
      }
      if (state.phone) return { phone_number: state.phone };
      return null;
    }

    markSynced() {
      this.state.retestSynced = true;
      var current = readStored();
      // A different session may own storage by now on a shared booth device.
      if (current && current.sessionId && current.sessionId !== this.state.sessionId) return;
      writeStored(this.state);
    }

    /* ---------- completion ---------- */

    finish() {
      document.body.classList.add('bl-retest-submitted');
      document.dispatchEvent(new CustomEvent('bl:event-retest:submitted', {
        detail: { state: this.state, properties: this.eventProperties() }, bubbles: true
      }));

      var target = this.root && this.root.dataset.resultUrl;
      if (!target) {
        this.setAttribute('data-submitted', 'true');
        return;
      }
      try {
        var url = new URL(target, window.location.href);
        if (url.protocol !== 'https:' && url.protocol !== 'http:') return;
        if (this.state.sessionId) url.searchParams.set('sid', this.state.sessionId);
        if (this.state.reading1) url.searchParams.set('r1', this.state.reading1);
        if (this.state.reading2) url.searchParams.set('r2', this.state.reading2);
        if (this.state.reading1At) url.searchParams.set('t', String(this.state.reading1At));
        window.location.assign(url.href);
      } catch (_) {
        this.setAttribute('data-submitted', 'true');
      }
    }
  });
})();
